import React, { useState, useEffect } from 'react';
import '../styles/TransporterProfile.css';
import AddPaymentModal from '../components/modals/AddPaymentModal';
import AccountStatement from './AccountStatement';
import BalanceSheet from './BalanceSheet';
import fetchApi from '../utils/api.js';
import Swal from 'sweetalert2';
import { FaEdit } from 'react-icons/fa';
import SearchBar from '../components/ui/SearchBar';
import { toast } from 'react-toastify';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import AirDatePicker from '../components/ui/AirDatePicker';

const TransporterProfile = ({ transporter, onBack, onPreviewInvoice, onEdit }) => {
    const { hasPermission } = usePermissionContext();
    const formatDate = (dateStr) => {
        if (!dateStr) return '---';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}-${m}-${y}`;
    };
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [paymentToEdit, setPaymentToEdit] = useState(null);
    const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0, balance: 0 });
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetails, setShowDetails] = useState(false);
    const [showStatement, setShowStatement] = useState(false);
    const [showBalanceSheet, setShowBalanceSheet] = useState(false);

    // --- Invoice List State ---
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoicePage, setInvoicePage] = useState(1);
    const [invoiceEntries, setInvoiceEntries] = useState(10);

    // --- Payment List State ---
    const [paymentSearch, setPaymentSearch] = useState('');
    const [paymentPage, setPaymentPage] = useState(1);
    const [paymentEntries, setPaymentEntries] = useState(10);

    // --- Date Filter State ---
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // --- Expense List State ---
    const [expenseSearch, setExpenseSearch] = useState('');
    const [expensePage, setExpensePage] = useState(1);
    const [expenseEntries, setExpenseEntries] = useState(10);

    // --- Kasar Payment List State ---
    const [kasarSearch, setKasarSearch] = useState('');
    const [kasarPage, setKasarPage] = useState(1);
    const [kasarEntries, setKasarEntries] = useState(10);

    useEffect(() => {
        if (transporter) {
            fetchTransporterData();
        }
    }, [transporter]);

    const fetchTransporterData = async () => {
        if (!transporter) return;
        try {
            setLoading(true);
            const tId = transporter.id || transporter.transporterId || transporter._id;
            if (!tId) return;

            let currentStart = startDate;
            let currentEnd = endDate;

            // Validation Logic
            if (currentStart && !currentEnd) {
                const today = new Date().toISOString().split('T')[0];
                currentEnd = today;
                setEndDate(today); // Sync UI
            } else if (!currentStart && currentEnd) {
                toast.warn('Start Date is required!');
                setLoading(false);
                return;
            }

            let queryParams = `?transporterId=${tId}`;
            if (currentStart && currentEnd) {
                queryParams += `&startDate=${currentStart}&endDate=${currentEnd}`;
            }

            // Fetch Totals
            if (hasPermission('Transporter', 'view')) {
                try {
                    const totalsData = await fetchApi(`/transporter-payment/totals/${tId}${currentStart && currentEnd ? `?startDate=${currentStart}&endDate=${currentEnd}` : ''}`);
                    setTotals(totalsData);
                } catch (err) {
                    console.warn("Transporter totals API error", err);
                }

                // Fetch Ledger
                try {
                    const ledger = await fetchApi(`/transporter-payment/ledger/${tId}${currentStart && currentEnd ? `?startDate=${currentStart}&endDate=${currentEnd}` : ''}`);

                    const invList = ledger
                        .filter(e => e.type === 'Sales Invoice' || e.type === 'Purchase Order')
                        .map(e => ({
                            id: e.refNo || e.id,
                            dbId: e.id,
                            date: e.date,
                            type: e.type,
                            place: transporter.transporterCity || transporter.transporter_city || '---',
                            amount: (e.debit || 0).toFixed(2)
                        }));
                    setInvoices(invList);
                } catch (err) {
                    console.warn("Transporter ledger API error", err);
                }
            } else { setTotals({ totalInvoiceAmount: 0, paidAmount: 0, pendingAmount: 0 }); setInvoices([]); }

            // Fetch Payments list separately
            if (hasPermission('Transporter', 'view')) {
                try {
                    const paymentsData = await fetchApi(`/transporter-payment${queryParams}`);
                    setPayments(paymentsData);
                } catch (err) {
                    console.warn("Transporter payments list API error", err);
                }
            } else { setPayments([]); }

            // Fetch Expenses
            if (hasPermission('Expense', 'view')) {
                try {
                    const expensesData = await fetchApi(`/expense?partyType=transporter&partyId=${tId}&limit=1000${currentStart && currentEnd ? `&startDate=${currentStart}&endDate=${currentEnd}` : ''}`);
                    setExpenses(expensesData.expenses || []);
                } catch (err) {
                    console.warn("Transporter expenses API error", err);
                }
            } else { setExpenses([]); }

        } catch (error) {
            console.error("Error fetching transporter profile data:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Helper Functions ---
    const filterData = (list, search, keys) => {
        if (!search) return list;
        return list.filter(item =>
            keys.some(key => item[key]?.toString().toLowerCase().includes(search.toLowerCase()))
        );
    };

    // Unified data mapping
    const displayData = {
        name: transporter?.transporterName || transporter?.transporter_name || '---',
        tradeName: transporter?.transporterTradeName || transporter?.transporter_trade_name || transporter?.transporterName || '---',
        referenceName: transporter?.transporterReferenceName || transporter?.transporter_reference_name || '---',
        email: transporter?.transporterEmail || transporter?.transporter_email || '---',
        mobileNumber: transporter?.transporterMobileNumber || transporter?.transporter_mobile || '---',
        gst: transporter?.transporterGst || transporter?.transporter_gst_number || '---',
        panNo: transporter?.transporterPanNo || transporter?.transporter_pan_no || '---',
        transporterType: transporter?.transporterType || transporter?.transporter_type || '---',
        country: transporter?.transporterCountry || transporter?.transporter_country || 'India',
        state: transporter?.transporterState || transporter?.transporter_state || '---',
        city: transporter?.transporterCity || transporter?.transporter_city || '---',
        pinCode: transporter?.transporterPinCode || transporter?.transporter_pin_code || '---',
        stateCode: transporter?.transporterStateCode || transporter?.transporter_state_code || '---',
        address: transporter?.transporterAddress || transporter?.transporter_address || '---'
    };

    // Filter Invoices
    const filteredInvoices = filterData(invoices, invoiceSearch, ['id', 'date', 'place', 'amount']);
    const invoiceLastIdx = invoicePage * invoiceEntries;
    const invoiceFirstIdx = invoiceLastIdx - invoiceEntries;
    const currentInvoices = filteredInvoices.slice(invoiceFirstIdx, invoiceLastIdx);

    // Filter Payments
    const filteredPayments = filterData(payments, paymentSearch, ['paymentAmount', 'paymentModeName', 'paymentCollectorName']);
    const paymentLastIdx = paymentPage * paymentEntries;
    const paymentFirstIdx = paymentLastIdx - paymentEntries;
    const currentPayments = filteredPayments.slice(paymentFirstIdx, paymentLastIdx);

    // Filter Expenses
    const filteredExpenses = filterData(expenses, expenseSearch, ['expenseName', 'invoiceNo', 'expensePurpose', 'date', 'expenseTypeName']);
    const expenseLastIdx = expensePage * expenseEntries;
    const expenseFirstIdx = expenseLastIdx - expenseEntries;
    const currentExpenses = filteredExpenses.slice(expenseFirstIdx, expenseLastIdx);

    // Filter Kasar Payments (where kasarAmount > 0)
    const kasarPayments = React.useMemo(() => {
        return payments.filter(pay => Number(pay.kasarAmount || pay.kasar_amount || 0) > 0);
    }, [payments]);

    const filteredKasarPayments = filterData(kasarPayments, kasarSearch, ['kasarAmount', 'paymentAmount', 'paymentModeName', 'remark']);
    const kasarLastIdx = kasarPage * kasarEntries;
    const kasarFirstIdx = kasarLastIdx - kasarEntries;
    const currentKasarPayments = filteredKasarPayments.slice(kasarFirstIdx, kasarLastIdx);

    const handleDeletePayment = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await fetchApi(`/transporter-payment/${id}`, { method: 'DELETE' });
                Swal.fire('Deleted!', 'Payment has been deleted.', 'success');
                fetchTransporterData();
            } catch (error) {
                console.error("Delete Error:", error);
                Swal.fire('Error!', 'Failed to delete payment.', 'error');
            }
        }
    };

    const handleEditPayment = (payment) => {
        setPaymentToEdit(payment);
        setIsPaymentModalOpen(true);
    };

    const handlePreviewClick = async (inv) => {
        try {
            setLoading(true);
            let data;
            if (inv.type === 'Sales Invoice') {
                data = await fetchApi(`/sales-invoice/${inv.dbId}`);
            } else if (inv.type === 'Purchase Order') {
                data = await fetchApi(`/purchase-order/${inv.dbId}`);
            }

            if (data && onPreviewInvoice) {
                // We pass the data and the type so the parent knows which preview to show
                onPreviewInvoice(data, inv.type === 'Sales Invoice' ? 'sale' : 'purchase');
            } else {
                Swal.fire('Error', 'Could not fetch invoice details', 'error');
            }
        } catch (error) {
            console.error("Preview Error:", error);
            Swal.fire('Error', 'Failed to fetch invoice details', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (showStatement) {
        return (
            <AccountStatement
                customer={{ ...displayData, tradeName: displayData.tradeName }}
                customerId={transporter?.id || transporter?.transporterId || transporter?._id}
                onBack={() => setShowStatement(false)}
                isTransporter={true}
                startDate={startDate}
                endDate={endDate}
            />
        );
    }

    if (showBalanceSheet) {
        return (
            <BalanceSheet
                customer={{ ...displayData, tradeName: displayData.tradeName }}
                customerId={transporter?.id || transporter?.transporterId || transporter?._id}
                isTransporter={true}
                startDate={startDate}
                endDate={endDate}
                onBack={() => setShowBalanceSheet(false)}
            />
        );
    }

    return (
        <div className="transporter-profile-container">
            <div className="transporter-profile-header">
                <h2 className="transporter-profile-title">
                    {(displayData.tradeName && displayData.tradeName !== '---') ? displayData.tradeName : (displayData.name || 'Transporter Profile')}
                </h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {hasPermission('Transporter', 'update') && onEdit && (
                        <button className="transporter-profile-back-btn" onClick={onEdit} style={{ backgroundColor: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <FaEdit /> Edit
                        </button>
                    )}
                    <button
                        className="transporter-profile-back-btn"
                        onClick={() => setShowDetails(!showDetails)}
                        style={{ backgroundColor: showDetails ? '#ef4444' : '#10b981', color: 'white' }}
                    >
                        {showDetails ? 'Closed Details' : 'Open Details'}
                    </button>
                    {onBack && (
                        <button className="transporter-profile-back-btn" onClick={onBack}>
                            ← Back
                        </button>
                    )}
                </div>
            </div>

            {/* Date Filter Section (Red box from user image) */}
            <div className="profile-date-filter-row">
                <div className="date-input-group">
                    <label>From Date:</label>
                    <AirDatePicker
                        value={startDate}
                        onChange={(dateStr) => setStartDate(dateStr)}
                    />
                </div>
                <div className="date-input-group">
                    <label>To Date:</label>
                    <AirDatePicker
                        value={endDate}
                        onChange={(dateStr) => setEndDate(dateStr)}
                    />
                </div>
                <div className="filter-actions-group">
                    <button className="btn-filter-apply" onClick={fetchTransporterData}>Apply Filter</button>
                    <button className="btn-filter-clear" onClick={() => { setStartDate(''); setEndDate(''); fetchTransporterData(); }}>Clear</button>
                </div>
            </div>

            {/* Top Details Section */}
            {showDetails && (
                <div className="transporter-profile-details-grid">
                    <div className="transporter-detail-card">
                        <div className="transporter-detail-card-title">Personal & Business Details</div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">Trade Name:</span>
                            <span className="transporter-detail-value">{displayData.tradeName}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">Transporter Name:</span>
                            <span className="transporter-detail-value">{displayData.name}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">Reference Name:</span>
                            <span className="transporter-detail-value">{displayData.referenceName}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">Email:</span>
                            <span className="transporter-detail-value">{displayData.email}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">Mobile:</span>
                            <span className="transporter-detail-value">{displayData.mobileNumber}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">GST:</span>
                            <span className="transporter-detail-value">{displayData.gst}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">PAN No:</span>
                            <span className="transporter-detail-value">{displayData.panNo}</span>
                        </div>
                    </div>

                    <div className="transporter-detail-card">
                        <div className="transporter-detail-card-title">Address Details</div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">Country:</span>
                            <span className="transporter-detail-value">{displayData.country}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">State:</span>
                            <span className="transporter-detail-value">{displayData.state}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">City:</span>
                            <span className="transporter-detail-value">{displayData.city}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">Pin Code:</span>
                            <span className="transporter-detail-value">{displayData.pinCode}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">State Code:</span>
                            <span className="transporter-detail-value">{displayData.stateCode}</span>
                        </div>
                        <div className="transporter-detail-row">
                            <span className="transporter-detail-label">Address:</span>
                            <span className="transporter-detail-value" style={{ maxWidth: '60%' }}>{displayData.address}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Statistics Row */}
            <div className="transporter-stats-row">
                <div className="transporter-stat-card">
                    <div className="transporter-stat-icon-wrapper stat-green">
                        📄
                    </div>
                    <div className="transporter-stat-info">
                        <h3>{(totals.totalCredit || 0).toFixed(2)}/-</h3>
                        <p className="transporter-stat-label">Credit</p>
                    </div>
                </div>
                <div className="transporter-stat-card">
                    <div className="transporter-stat-icon-wrapper stat-blue">
                        💵
                    </div>
                    <div className="transporter-stat-info">
                        <h3>{(totals.totalDebit || 0).toFixed(2)}/-</h3>
                        <p className="transporter-stat-label">Debit</p>
                    </div>
                </div>
                <div className="transporter-stat-card">
                    <div className="transporter-stat-icon-wrapper stat-red">
                        📱
                    </div>
                    <div className="transporter-stat-info">
                        <h3>{(totals.balance || 0).toFixed(2)}/-</h3>
                        <p className="transporter-stat-label">
                            {totals.balance > 0 ? 'Pending' : totals.balance < 0 ? 'Advance' : 'Balance'}
                        </p>
                    </div>
                </div>

                <div className="transporter-profile-actions">
                    <button className="transporter-action-btn" onClick={() => setIsPaymentModalOpen(true)}>Add Payment</button>
                    <button className="transporter-action-btn" onClick={() => setShowBalanceSheet(true)}>Balance Sheet</button>
                    <button className="transporter-action-btn" onClick={() => setShowStatement(true)}>Account Statement</button>
                </div>
            </div>

            <div className="transporter-profile-tables-grid" style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%' }}>
                {hasPermission('Transporter', 'view') && (
                <div className="transporter-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="transporter-list-header" style={{ margin: 0 }}>LR / Invoice List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={invoiceSearch}
                                onChange={(e) => {
                                    setInvoiceSearch(e.target.value);
                                    setInvoicePage(1);
                                }}
                                placeholder="Search invoices..."
                            />
                        </div>
                    </div>

                    <table className="transporter-profile-table">
                        <thead>
                            <tr>
                                <th>LR / INV NO</th>
                                <th>DATE</th>
                                <th>AREA</th>
                                <th>AMOUNT</th>
                                <th>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentInvoices.length > 0 ? currentInvoices.map(inv => (
                                <tr key={inv.id}>
                                    <td>{inv.id}</td>
                                    <td>{inv.date}</td>
                                    <td>{inv.place}</td>
                                    <td>{inv.amount}</td>
                                    <td>
                                        <button
                                            className="list-page-icon-btn"
                                            title="View"
                                            onClick={() => handlePreviewClick(inv)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                <circle cx="12" cy="12" r="3"></circle>
                                            </svg>
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={5} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={invoiceEntries}
                                onChange={(e) => {
                                    setInvoiceEntries(Number(e.target.value));
                                    setInvoicePage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredInvoices.length === 0 ? 0 : invoiceFirstIdx + 1} to {Math.min(invoiceLastIdx, filteredInvoices.length)} of {filteredInvoices.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={invoicePage <= 1}
                                onClick={() => setInvoicePage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredInvoices.length / invoiceEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === invoicePage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setInvoicePage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={invoicePage >= Math.ceil(filteredInvoices.length / invoiceEntries)}
                                onClick={() => setInvoicePage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {hasPermission('Transporter', 'view') && (
                <div className="transporter-list-section full-width">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="transporter-list-header" style={{ margin: 0 }}>Payment List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={paymentSearch}
                                onChange={(e) => {
                                    setPaymentSearch(e.target.value);
                                    setPaymentPage(1);
                                }}
                                placeholder="Search payments..."
                            />
                        </div>
                    </div>

                    <table className="transporter-profile-table">
                        <thead>
                            <tr>
                                <th>AMOUNT</th>
                                <th>DATE TIME</th>
                                <th>MODE</th>
                                <th>COLLECTOR</th>
                                <th>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPayments.length > 0 ? currentPayments.map((pay, idx) => (
                                <tr key={idx}>
                                    <td>{Number(pay.paymentAmount).toFixed(2)}</td>
                                    <td>{pay.paymentDate} {pay.paymentTime}</td>
                                    <td>{pay.paymentModeName}</td>
                                    <td>{pay.paymentCollectorName || 'Authorized'}</td>
                                    <td>
                                        <div className="list-page-action-icons">
                                            {hasPermission('Transporter', 'update') && (
                                                <button className="list-page-icon-btn" title="Edit" onClick={() => handleEditPayment(pay)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                            )}
                                            {hasPermission('Transporter', 'delete') && (
                                                <button className="list-page-icon-btn" title="Delete" onClick={() => handleDeletePayment(pay._id)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={5} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={paymentEntries}
                                onChange={(e) => {
                                    setPaymentEntries(Number(e.target.value));
                                    setPaymentPage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredPayments.length === 0 ? 0 : paymentFirstIdx + 1} to {Math.min(paymentLastIdx, filteredPayments.length)} of {filteredPayments.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={paymentPage <= 1}
                                onClick={() => setPaymentPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredPayments.length / paymentEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === paymentPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setPaymentPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={paymentPage >= Math.ceil(filteredPayments.length / paymentEntries)}
                                onClick={() => setPaymentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Kasar Payment List */}
                {hasPermission('Transporter', 'view') && (
                <div className="transporter-list-section" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="transporter-list-header" style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>Kasar Payment List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={kasarSearch}
                                onChange={(e) => {
                                    setKasarSearch(e.target.value);
                                    setKasarPage(1);
                                }}
                                placeholder="Search kasar payments..."
                            />
                        </div>
                    </div>

                    <table className="transporter-profile-table">
                        <thead>
                            <tr>
                                <th>KASAR AMOUNT</th>
                                <th>PAYMENT AMOUNT</th>
                                <th>DATE TIME</th>
                                <th>MODE</th>
                                <th>REMARK</th>
                                <th>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentKasarPayments.length > 0 ? currentKasarPayments.map((pay, idx) => (
                                <tr key={idx}>
                                    <td style={{ fontWeight: '600', color: '#dc2626' }}>₹{Number(pay.kasarAmount || pay.kasar_amount || 0).toFixed(2)}</td>
                                    <td>₹{Number(pay.paymentAmount).toFixed(2)}</td>
                                    <td>{pay.paymentDate} {pay.paymentTime}</td>
                                    <td>{pay.paymentModeName}</td>
                                    <td>{pay.remark || '---'}</td>
                                    <td>
                                        <div className="list-page-action-icons">
                                            {hasPermission('Transporter', 'update') && (
                                                <button className="list-page-icon-btn" title="Edit" onClick={() => handleEditPayment(pay)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                </button>
                                            )}
                                            {hasPermission('Transporter', 'delete') && (
                                                <button className="list-page-icon-btn" title="Delete" onClick={() => handleDeletePayment(pay._id)}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={6} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={kasarEntries}
                                onChange={(e) => {
                                    setKasarEntries(Number(e.target.value));
                                    setKasarPage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredKasarPayments.length === 0 ? 0 : kasarFirstIdx + 1} to {Math.min(kasarLastIdx, filteredKasarPayments.length)} of {filteredKasarPayments.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={kasarPage <= 1}
                                onClick={() => setKasarPage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredKasarPayments.length / kasarEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === kasarPage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setKasarPage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={kasarPage >= Math.ceil(filteredKasarPayments.length / kasarEntries)}
                                onClick={() => setKasarPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Expense List */}
                {hasPermission('Expense', 'view') && (
                <div className="transporter-list-section" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                        <div className="transporter-list-header" style={{ margin: 0, fontWeight: '600', fontSize: '18px' }}>Expense List</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <SearchBar
                                value={expenseSearch}
                                onChange={(e) => {
                                    setExpenseSearch(e.target.value);
                                    setExpensePage(1);
                                }}
                                placeholder="Search expenses..."
                            />
                        </div>
                    </div>

                    <table className="transporter-profile-table">
                        <thead>
                            <tr>
                                <th>EXPENSE TYPE</th>
                                <th>DATE</th>
                                <th>EXPENSE NAME</th>
                                <th>INVOICE NO</th>
                                <th>NET AMOUNT</th>
                                <th>GST AMOUNT</th>
                                <th>TOTAL AMOUNT</th>
                                <th>PURPOSE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentExpenses.length > 0 ? currentExpenses.map((exp, idx) => (
                                <tr key={exp.id || idx}>
                                    <td style={{ fontWeight: '500', color: '#1e3a8a' }}>{exp.expenseTypeName || '---'}</td>
                                    <td>{formatDate(exp.date)}</td>
                                    <td style={{ fontWeight: '500' }}>{exp.expenseName || '---'}</td>
                                    <td>{exp.invoiceNo || '---'}</td>
                                    <td>₹{parseFloat(exp.netAmount || 0).toFixed(2)}</td>
                                    <td>₹{parseFloat(exp.gstAmount || 0).toFixed(2)}</td>
                                    <td style={{ fontWeight: '600' }}>₹{parseFloat(exp.rowTotal || 0).toFixed(2)}</td>
                                    <td>{exp.expensePurpose || '---'}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan={8} className="no-data">No data available in table</td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="list-page-footer">
                        <div className="list-page-entries">
                            <span>Show</span>
                            <select
                                className="list-page-entries-select"
                                value={expenseEntries}
                                onChange={(e) => {
                                    setExpenseEntries(Number(e.target.value));
                                    setExpensePage(1);
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>

                        <div className="list-page-info">
                            Showing {filteredExpenses.length === 0 ? 0 : expenseFirstIdx + 1} to {Math.min(expenseLastIdx, filteredExpenses.length)} of {filteredExpenses.length} entries
                        </div>

                        <div className="list-page-pagination">
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={expensePage <= 1}
                                onClick={() => setExpensePage(p => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: Math.ceil(filteredExpenses.length / expenseEntries) }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    type="button"
                                    className={`list-page-page-num ${p === expensePage ? 'list-page-page-num-active' : ''}`}
                                    onClick={() => setExpensePage(p)}
                                >
                                    {p}
                                </button>
                            ))}
                            <button
                                type="button"
                                className="list-page-page-btn"
                                disabled={expensePage >= Math.ceil(filteredExpenses.length / expenseEntries)}
                                onClick={() => setExpensePage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
                )}

            </div>

            <AddPaymentModal
                isOpen={isPaymentModalOpen}
                onClose={() => {
                    setIsPaymentModalOpen(false);
                    setPaymentToEdit(null);
                    fetchTransporterData();
                }}
                entityName={displayData.name}
                entityType="Transporter"
                entityId={transporter?.id || transporter?.transporterId || transporter?._id}
                paymentToEdit={paymentToEdit}
            />
        </div>
    );
};

export default TransporterProfile;
